import ClearIcon from "@mui/icons-material/Clear";
import { Box, Stack, Tooltip, styled } from "@mui/material";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import ButtonBackground from "images/button-background.svg";
import ButtonIdleActive from "images/button-idle-active.svg";
import ButtonIdleHover from "images/button-idle-hover.svg";
import ButtonIdle from "images/button-idle.svg";
import ButtonSuccess from "images/button-success.svg";
import { ReactComponent as EditLogoIcon } from "images/edit-avatar-icon.svg";
import { UPLOAD_FILE_SIZE_LIMIT_IN_BYTES } from "models/constants";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { HelpIcon } from "UI/HelpIcon";
import { ButtonSkinGuideLines } from "./ButtonSkinGuideLines";
import * as SC from "./RecipientDetails.style";

const LogoHolder = styled(Stack)`
  flex: 0 0 auto;
  width: 87px;
  height: 87px;

  justify-content: center;
  align-items: center;

  background-color: transparent;
  border-radius: 50%;
  overflow: hidden;

  user-select: none;
`;

const HalfHolder = styled(Stack)`
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  height: 120px;

  justify-content: center;
  align-items: center;

  border-top-left-radius: 48px;
  border-top-right-radius: 48px;
  overflow: hidden;

  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  user-select: none;
`;

const TextMosaic = styled(Box)`
  width: 140px;
  height: 140px;

  font-size: 10px;
  font-weight: 600;

  color: lightgrey;
  background-color: white;
  word-break: break-all;
  overflow-wrap: anywhere;
  word-wrap: anywhere;

  transform: rotate(-10deg);
`;

const ActionButton = styled(Stack)`
  width: 27px;
  height: 27px;
  border-radius: 50%;

  justify-content: center;
  align-items: center;

  background-color: gray;
  cursor: pointer;
  &:hover {
    background-color: #666;
  }
`;

export const FileInput = styled("input")`
  height: 1px;
  width: 1px;

  overflow: hidden;
`;

export const Logo = styled(Box)`
  width: 157px;
  height: 87px;

  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;

  cursor: pointer;
  border-radius: 6px;
`;

const ButtonsHolder = styled(Stack)`
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 5px;

  position: absolute;
  bottom: -42px;
  right: -2px;
`;

export const InputHolder = styled("label")`
  cursor: pointer;
`;

const modes = {
  Skin: "Skin",
  Half: "Half",
  Logo: "Logo",
};

export const ButtonContainer = styled(Stack)`
  position: relative;
  width: 239px;
  height: 239px;

  justify-content: center;
  align-items: center;
  gap: 0;

  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  border-radius: 60px;
`;

export const Led = styled(Box)`
  margin-top: 3px;
  margin-bottom: 1px;
  height: 26px;
  width: 9px;

  background-color: darkgrey;
  border-radius: 3px;

  &.idle {
    background-color: lightgrey;
  }

  &.success {
    background-color: #72ff13;
  }

  z-index: 10;
  border: 1px solid darkgrey;
`;

export const ButtonIndicator = styled(Stack)`
  width: 87px;
  height: 87px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  cursor: pointer;
  user-select: none;

  &.idle {
    background-image: url(${ButtonIdle});
    background-position: center;
    background-repeat: no-repeat;
  }

  &.idle:hover {
    background-image: url(${ButtonIdleHover});
    background-position: center;
    background-repeat: no-repeat;
  }

  &.idle:active {
    background-image: url(${ButtonIdleActive});
    background-position: center;
    background-repeat: no-repeat;
  }

  &.success,
  &.success:hover {
    background-image: url(${ButtonSuccess});
    background-position: center;
    background-repeat: no-repeat;
  }
  z-index: 10;
`;

export const ButtonPreview = ({ name, id, imageFromS3 }) => {
  const dispatch = useAppDispatch();
  const { createApiCall } = useCreateApiCall();
  const [profileImage, setProfileImage] = useState(null);
  const [state, setState] = useState("idle");
  const [mode, setMode] = useState(modes.Logo);
  const imageUploadInput = useRef();

  const getLogo = createApiCall(async () => {
    const imageUrl = imageFromS3?.awsKey
      ? `${process.env.REACT_APP_API_V2_URL}/organizations/skins/${id}`
      : null;

    if (!imageUrl) {
      const result = await API.get("restApi", `/download_logo/${id}`, {});

      if (result?.error) {
        dispatch(showError(result?.error));
      }
      setProfileImage(result?.image);
      setMode(result?.mode ?? modes.Skin);
    } else {
      setProfileImage(imageUrl);
      setMode(imageFromS3?.mode);
    }
  });

  const handleUpload = async () => {
    // @ts-ignore
    const newFile = imageUploadInput?.current?.files?.[0];
    const size = newFile?.size;

    if (size > UPLOAD_FILE_SIZE_LIMIT_IN_BYTES) {
      dispatch(showError("Your file size is larger than 4MB."));
      // @ts-ignore
      if (imageUploadInput?.current?.value) {
        // @ts-ignore
        imageUploadInput.current.value = "";
      }
      return;
    }

    if (newFile) {
      const reader = new FileReader();
      reader.addEventListener(
        "load",
        () => {
          const imageString = reader.result.toString();
          if (imageString.length > UPLOAD_FILE_SIZE_LIMIT_IN_BYTES) {
            dispatch(
              showError(
                `The image file is too big. It should be lower than 4MB.`
              )
            );
            // @ts-ignore
            imageUploadInput.current.value = null;
            return;
          }
          const data = new FormData();
          // data.append("organizationId", id);
          // const blob = new Blob([imageString], { type: "text/plain" });
          data.append("file", newFile);
          data.append("dashboardAccessToken", null);
          data.append("mode", modes.Skin);

          const uploadLogo = createApiCall(async () => {
            const result = await API.post(
              "restApiV2",
              `/organizations/skins/${id}`,
              {
                body: data,
              }
            );

            if (result?.error) {
              dispatch(showError(result?.message || result.error));
              setProfileImage(null);
              if (imageUploadInput.current) {
                // @ts-ignore
                imageUploadInput.current.value = "";
              }
              return;
            }
            setProfileImage(reader.result);
          });

          uploadLogo();
        },
        false
      );
      reader.readAsDataURL(newFile);
    }
  };

  // const onChangeMode = (event) => {
  //   const newMode = event?.target?.getAttribute("data-value");
  //   setMode(newMode);

  //   const data = new FormData();
  //   data.append("organizationId", id);
  //   data.append("mode", newMode);

  //   const uploadLogo = createApiCall(async () => {
  //     const result = await API.post("restApi", `/upload_logo`, {
  //       body: data,
  //     });

  //     if (result?.error) {
  //       dispatch(showError(result?.error));
  //       // @ts-ignore
  //       imageUploadInput.current.value = null;
  //       return;
  //     }
  //   });

  //   uploadLogo();
  // };

  const onClearImage = () => {
    const deleteLogo = createApiCall(async () => {
      const result = await API.post(
        "restApiV2",
        `/organizations/skins/${id}/delete`,
        {
          body: {
            dashboardAccessToken: null,
          },
        }
      );

      if (result?.error) {
        dispatch(showError(result?.message || result.error));
        return;
      }

      setProfileImage(null);
      if (imageUploadInput.current) {
        // @ts-ignore
        imageUploadInput.current.value = "";
      }
    });

    deleteLogo();
  };

  useEffect(() => {
    getLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setState("idle"), 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [state]);

  const isSkinPresent = mode === modes.Skin && !!profileImage;
  const isHalfPresent = mode === modes.Half && !!profileImage;
  const isLogoPresent = mode === modes.Logo && !!profileImage;

  return (
    <Stack justifyContent={"center"} alignItems={"center"} gap={2}>
      <Stack alignItems={"center"} gap={2}>
        <div className="flex felx-row justify-start items-center gap-3">
          <SC.SubTitle>Digital Button Skin</SC.SubTitle>
          <HelpIcon>
            <ButtonSkinGuideLines />
          </HelpIcon>
        </div>
        <ButtonContainer
          sx={{
            backgroundImage: isSkinPresent
              ? `url("${profileImage}")`
              : `url("${ButtonBackground}")`,
            border: "1px solid #d0d0d0",
            borderRadius: "66px",
          }}
        >
          {(isSkinPresent || isHalfPresent) && <LogoHolder />}
          {!isSkinPresent && !isLogoPresent && !isHalfPresent && (
            <LogoHolder>
              <TextMosaic>
                {[...Array(40)].map((value, index) => name).join(" ")}
              </TextMosaic>
            </LogoHolder>
          )}
          {isHalfPresent && (
            <HalfHolder
              sx={{
                backgroundImage: `url("${profileImage}")`,
              }}
            />
          )}
          {isLogoPresent && (
            <Logo
              sx={{
                backgroundImage: `url("${profileImage}")`,
              }}
            />
          )}
          <Led className={state} />
          <ButtonIndicator
            className={state}
            onClick={() => setState("success")}
          ></ButtonIndicator>
          <ButtonsHolder>
            <InputHolder htmlFor="image-input">
              <Tooltip title="Upload Image">
                <ActionButton>
                  <EditLogoIcon />
                </ActionButton>
              </Tooltip>
              <FileInput
                ref={imageUploadInput}
                id="image-input"
                name="preview"
                onChange={handleUpload}
                type="file"
                multiple={false}
                accept="image/png, image/jpeg, image/svg"
              />
            </InputHolder>
            {!!profileImage && (
              <Tooltip title="Clear Image">
                <ActionButton onClick={onClearImage}>
                  <ClearIcon
                    sx={{
                      "& path": {
                        fill: "white",
                      },
                    }}
                  />
                </ActionButton>
              </Tooltip>
            )}
          </ButtonsHolder>
        </ButtonContainer>
      </Stack>
      {/* <Stack>
        <FormLabel htmlFor="mode">Button Image Placement</FormLabel>
        <TextField
          select
          id="mode"
          value={mode}
          sx={{ width: "200px" }}
          disabled={!profileImage}
        >
          {Object.keys(modes).map((key) => (
            <MenuItem onClick={onChangeMode} key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </TextField>
      </Stack> */}
    </Stack>
  );
};
